import React, { useState, useEffect } from 'react';
import DataService from '../../services/data.service';
import LoaderOverlay from '../common/loader.component';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const SeoPageForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pageData = location.state?.data;
  const isEdit = !!pageData;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    status: 'draft',
    display_order: 999,
    show_in_popular: false,
    show_in_services: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pageData) {
      setFormData({
        id: pageData.id,
        title: pageData.title || '',
        slug: pageData.slug || '',
        content: pageData.content || '',
        meta_title: pageData.meta_title || '',
        meta_description: pageData.meta_description || '',
        meta_keywords: pageData.meta_keywords || '',
        status: pageData.status || 'draft',
        display_order: pageData.display_order || 999,
        show_in_popular: pageData.show_in_popular || false,
        show_in_services: pageData.show_in_services || true
      });
    }
  }, [pageData]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData({
      ...formData,
      title: newTitle,
      slug: !isEdit ? generateSlug(newTitle) : formData.slug
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title is required.');
      return;
    }

    if (!formData.slug.trim()) {
      setError('Slug is required.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        show_in_popular: formData.show_in_popular ? 1 : 0,
        show_in_services: formData.show_in_services ? 1 : 0
      };

      if (isEdit) {
        await DataService.editSeoPage(payload);
        toast.success('SEO Page updated successfully.');
      } else {
        await DataService.addSeoPage(payload);
        toast.success('SEO Page added successfully.');
      }
      navigate('/seo-pages');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save page.');
    } finally {
      setLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <>
      <LoaderOverlay loading={loading} />

      <h4>{isEdit ? 'Edit' : 'Add New'} SEO Page</h4>

      <form onSubmit={handleSubmit} className="mt-3">
        <div className="row">
          <div className="col-md-8">
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Page Content</h6>

                <div className="mb-3">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    className="form-control px-3"
                    placeholder="Enter page title"
                    value={formData.title}
                    onChange={handleTitleChange}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Slug *</label>
                  <input
                    type="text"
                    className="form-control px-3"
                    placeholder="page-url-slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  />
                  <small className="text-muted">URL: /{formData.slug}</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Content</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={quillModules}
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h6 className="card-title">SEO Metadata</h6>

                <div className="mb-3">
                  <label className="form-label">Meta Title</label>
                  <input
                    type="text"
                    className="form-control px-3"
                    placeholder="SEO meta title (recommended: 50-60 characters)"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    maxLength="60"
                  />
                  <small className="text-muted">{formData.meta_title.length}/60 characters</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    className="form-control px-3"
                    rows="3"
                    placeholder="SEO meta description (recommended: 150-160 characters)"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    maxLength="160"
                  />
                  <small className="text-muted">{formData.meta_description.length}/160 characters</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Meta Keywords</label>
                  <input
                    type="text"
                    className="form-control px-3"
                    placeholder="keyword1, keyword2, keyword3"
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData({ ...formData, meta_keywords: e.target.value })}
                  />
                  <small className="text-muted">Comma-separated keywords</small>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Publish Settings</h6>

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-control px-3"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Display Order</label>
                  <input
                    type="number"
                    className="form-control px-3"
                    placeholder="999"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 999 })}
                  />
                  <small className="text-muted">Lower numbers appear first</small>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h6 className="card-title">Display Options</h6>

                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="showInPopular"
                    checked={formData.show_in_popular}
                    onChange={(e) => setFormData({ ...formData, show_in_popular: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="showInPopular">
                    Show in Popular Links
                  </label>
                  <br />
                  <small className="text-muted">Display in top 4 popular links</small>
                </div>

                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="showInServices"
                    checked={formData.show_in_services}
                    onChange={(e) => setFormData({ ...formData, show_in_services: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="showInServices">
                    Show in Services Menu
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger mt-3">{error}</div>}

        <div className="mt-3">
          <button type="submit" className="btn btn-primary">
            {isEdit ? 'Update' : 'Create'} Page
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ marginLeft: '15px' }}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      </form>
    </>
  );
};

export default SeoPageForm;
